import React, { useEffect, useMemo, useState } from 'react';
import { createAdminInvite, listAdminInvites, resendAdminInvite, cancelAdminInvite } from '../services/adminInvitationsApi';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import { PageSkeleton } from '../../../components/loading/skeletons';
import './AdminInvitesSection.css';

export default function AdminInvitesSection({ showHeader = true }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('support');
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const acceptBaseUrl = useMemo(() => `${window.location.origin}${adminPath(ADMIN_PATHS.INVITE_ACCEPT)}?token=`, []);

  const load = async () => {
    try {
      setLoadingList(true);
      setError('');
      const invites = await listAdminInvites({ status: 'pending' });
      setItems(invites);
    } catch (e) {
      setError(e?.message || 'Failed to load invites');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    try {
      setLoadingCreate(true);
      setError('');
      setSuccess('');
      const res = await createAdminInvite({ email, role });
      setSuccess(`Invite sent to ${email}`);
      setEmail('');
      await load();
      // Best-effort copy link (useful during testing)
      try {
        await navigator.clipboard.writeText(`${acceptBaseUrl}${encodeURIComponent(res.token)}`);
      } catch {
        // ignore
      }
    } catch (e) {
      setError(e?.message || 'Failed to create invite');
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleResend = async (token) => {
    try {
      setError('');
      await resendAdminInvite({ token });
      setSuccess('Invite resent');
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to resend invite');
    }
  };

  const handleCancel = async (token) => {
    if (!window.confirm('Cancel this invite?')) return;
    try {
      setError('');
      await cancelAdminInvite({ token });
      setSuccess('Invite cancelled');
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to cancel invite');
    }
  };

  const handleCopyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(`${acceptBaseUrl}${encodeURIComponent(token)}`);
      setSuccess('Invite link copied');
    } catch {
      setError('Failed to copy link');
    }
  };

  return (
    <div className="admin-invites-section">
      {showHeader && (
        <>
          <h2 className="admin-invites-header-title">Support/Admin Invites</h2>
          <p className="admin-invites-header-subtitle">
            Invite support staff (or admins) by email to access the admin panel. They'll set their own password from the email link.
          </p>
        </>
      )}

      {error && (
        <div className="admin-invites-alert admin-invites-alert-error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="admin-invites-alert admin-invites-alert-success">
          <span>✓</span>
          <span>{success}</span>
        </div>
      )}

      <div className="admin-invites-card">
        <h3 className="admin-invites-card-title">Create Invite</h3>
        <div className="admin-invites-form-row">
          <div className="admin-invites-form-group">
            <label className="admin-invites-label">Email</label>
            <input
              type="email"
              className="admin-invites-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div className="admin-invites-form-group">
            <label className="admin-invites-label">Role</label>
            <select
              className="admin-invites-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="support">Support</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            className="admin-invites-btn admin-invites-btn-primary"
            onClick={handleCreate}
            disabled={loadingCreate || !email}
          >
            {loadingCreate ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
      </div>

      <div className="admin-invites-card">
        <div className="admin-invites-card-header">
          <h3 className="admin-invites-card-title">Pending Invites</h3>
          <button
            className="admin-invites-refresh-btn"
            onClick={load}
            disabled={loadingList}
            title="Refresh"
          >
            {loadingList ? '⟳' : '⟳'}
          </button>
        </div>
        <div className="admin-invites-divider"></div>

        {loadingList && items.length === 0 ? (
          <PageSkeleton showHeader={false} rows={4} label="Loading invites" />
        ) : items.length === 0 ? (
          <p className="admin-invites-empty">No pending invites.</p>
        ) : (
          <div className="admin-invites-list">
            {items.map((inv) => (
              <div key={inv.token} className="admin-invites-item">
                <div className="admin-invites-item-content">
                  <div className="admin-invites-item-info">
                    <div className="admin-invites-item-email">{inv.email}</div>
                    <div className="admin-invites-item-token">Token: {inv.token}</div>
                  </div>
                  <span className={`admin-invites-role-badge admin-invites-role-${inv.role}`}>
                    {inv.role.charAt(0).toUpperCase() + inv.role.slice(1)}
                  </span>
                </div>
                <div className="admin-invites-item-actions">
                  <button
                    className="admin-invites-action-btn"
                    onClick={() => handleCopyLink(inv.token)}
                    title="Copy invite link"
                  >
                    📋
                  </button>
                  <button
                    className="admin-invites-action-btn"
                    onClick={() => handleResend(inv.token)}
                    title="Resend invite"
                  >
                    ✉️
                  </button>
                  <button
                    className="admin-invites-action-btn admin-invites-action-btn-danger"
                    onClick={() => handleCancel(inv.token)}
                    title="Cancel invite"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
